import { ReactNode, SyntheticEvent, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { Upload as UploadIcon } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/AddCircle';
import EventBusy from '@mui/icons-material/EventBusy';
import LocalShipping from '@mui/icons-material/LocalShipping';
import ReportProblem from '@mui/icons-material/ReportProblem';
import PersonOff from '@mui/icons-material/PersonOff';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  AddODSModel,
  client,
  DeliveryScheduleFilterOptions,
  DeliveryScheduleModel,
  MarketOrderType,
  MarketOrderTypeFilterOptions,
  MarketStoreFilterOptions,
  MarketSupplier,
  ODSImportResultModel,
} from '../../../../app/services/api/orderManagementClient';
import { Store } from '../../../../app/services/api/generated';
import { UserContext } from '../../../../components/shared/useUser';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import { ConfirmationDialog } from '../../../../components/shared/dialogs/ConfirmationDialog';
import DatePickerLocalized from '../../../../components/shared/DatePickerLocalized';
import { QuickFilterBar, QuickFilterGroup } from '../../../../components/shared/filters/QuickFilterBar';
import { TableColumn, TableSectionProps } from '../../../../components/shared/tables/TableSection';
import { InsightTile } from '../../../../components/shared/tiles/InsightTile';
import type { ListPageHeadline } from '../../../../components/page-templates/list-page/ListPageTemplate';
import { Trash01 } from '../../../../assets';
import { filterStoreOptions, base64ToBlob } from '../../../../utils';
import { isAutoShipOrder, isOrderTypeDSD } from '../../../../enums/OrderType';
import { useInstanceMarket } from '../../../../contexts/InstanceMarketContext';
import ODSUploadModal from '../../../ManageDeliverySchedule/modals/ODSUploadModal';
import DateSelectionModal from '../../../ManageDeliverySchedule/modals/DateSelectionModal';
import AddODSModal from '../../../ManageDeliverySchedule/modals/AddODSModal';

type Props = {
  onFilterChange?: (instance: string, market: string, filters?: {
    orderTypes?: string;
    supplierNumber?: string;
    deliveryDateFrom?: string;
    deliveryDateTo?: string;
    cutOffDateFrom?: string;
    cutOffDateTo?: string;
  }) => void;
  initialInstance?: string;
  initialMarket?: string;
  initialOrderTypes?: string;
  initialSupplier?: string;
  initialDeliveryDateFrom?: string;
  initialDeliveryDateTo?: string;
  initialCutOffDateFrom?: string;
  initialCutOffDateTo?: string;
  children: (parts: {
    filterBar: ReactNode;
    quickFilterContent: ReactNode;
    advancedFilterContent: ReactNode;
    advancedFilterActions: ReactNode;
    actions: ReactNode;
    headlineContent: ListPageHeadline;
    table: TableSectionProps<DeliveryScheduleModel>;
    isInitialLoading: boolean;
  }) => ReactNode;
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;

type DateQuickFilterPreset = 'today' | 'tomorrow' | 'thisWeek' | 'thisMonth';

const DATE_QUICK_FILTER_PRESETS: DateQuickFilterPreset[] = ['today', 'tomorrow', 'thisWeek', 'thisMonth'];

// Presets write into the same From/To state the exact date pickers use, so either can drive the other.
const getDateQuickFilterRange = (preset: DateQuickFilterPreset): [string, string] => {
  const today = dayjs().startOf('day');
  switch (preset) {
    case 'today':
      return [today.format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
    case 'tomorrow': {
      const tomorrow = today.add(1, 'day');
      return [tomorrow.format('YYYY-MM-DD'), tomorrow.format('YYYY-MM-DD')];
    }
    case 'thisWeek':
      return [today.format('YYYY-MM-DD'), today.endOf('week').format('YYYY-MM-DD')];
    case 'thisMonth':
      return [today.format('YYYY-MM-DD'), today.endOf('month').format('YYYY-MM-DD')];
  }
};

const findDateQuickFilterPreset = (from: string, to: string): DateQuickFilterPreset | undefined => (
  DATE_QUICK_FILTER_PRESETS.find((preset) => {
    const [presetFrom, presetTo] = getDateQuickFilterRange(preset);
    return from === presetFrom && to === presetTo;
  })
);

const DeliverySchedulesListPageContent = ({
  onFilterChange,
  initialInstance,
  initialMarket,
  initialOrderTypes,
  initialSupplier,
  initialDeliveryDateFrom,
  initialDeliveryDateTo,
  initialCutOffDateFrom,
  initialCutOffDateTo,
  children,
}: Props) => {
  const { t } = useTranslation('odsmanagement');
  const { hasPermissionTo, isAdmin, selectedStore } = useContext(UserContext);
  const hasWritePermissions = hasPermissionTo(['ODSWrite']) || isAdmin();
  const hasDeletePermissions = hasPermissionTo(['ODSDelete']);

  const { instances, instancesLoading, getMarkets, ensureMarketsLoaded, isMarketsLoading, defaultFiltersLoading } = useInstanceMarket();
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [selectedMarket, setSelectedMarket] = useState<string>('');
  const markets = getMarkets(selectedInstance);

  const [orderTypes, setOrderTypes] = useState<MarketOrderType[]>([]);
  const [selectedOrderTypes, setSelectedOrderTypes] = useState<MarketOrderType[]>([]);

  const [suppliers, setSuppliers] = useState<MarketSupplier[]>([]);
  const [supplier, setSupplier] = useState<string>('');

  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [checkedAllStores, setCheckedAllStores] = useState<boolean>(false);

  const [deliveryDateFrom, setDeliveryDateFrom] = useState('');
  const [deliveryDateTo, setDeliveryDateTo] = useState('');
  const [cutOffDateFrom, setCutOffDateFrom] = useState('');
  const [cutOffDateTo, setCutOffDateTo] = useState('');

  // Options (order types/suppliers/stores) load as soon as Instance+Market are known, independent of the table search.
  const [isOptionsLoading, setIsOptionsLoading] = useState<boolean>(false);
  const [hasLoadedOptionsOnce, setHasLoadedOptionsOnce] = useState<boolean>(false);
  // Guards one-time hydration of quick/advanced filters from the URL once order types are available.
  const hasHydratedFiltersFromUrl = useRef(false);

  const [list, setList] = useState<DeliveryScheduleModel[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasSearchedOnce, setHasSearchedOnce] = useState<boolean>(false);
  const pageSize = 10;

  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);

  // Headline tiles summarise the whole Instance/Market scope, fetched once per scope change so they aren't tied to the table's search/paging/sort.
  const [statsList, setStatsList] = useState<DeliveryScheduleModel[]>([]);
  const [isStatsLoading, setIsStatsLoading] = useState<boolean>(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [currentFileRequest, setCurrentFileRequest] = useState<{ fileData: string; fileName: string } | null>(null);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [dateModalType, setDateModalType] = useState<'cutoff' | 'delivery'>('cutoff');
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [createODSModalOpen, setCreateODSModalOpen] = useState(false);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Instance/market default resolution + URL sync lives in the page's index.tsx, matching Suppliers/Products.
  useEffect(() => {
    if (initialInstance) setSelectedInstance(initialInstance);
    if (initialMarket) setSelectedMarket(initialMarket);
  }, [initialInstance, initialMarket]);

  // No store-based default (e.g. no current store) and nothing in the URL - if there's only one Instance at all, it's unambiguous.
  useEffect(() => {
    if (selectedInstance || defaultFiltersLoading || instancesLoading) return;

    if (instances.length === 1 && instances[0].id != null) {
      const onlyInstance = String(instances[0].id);
      setSelectedInstance(onlyInstance);
      onFilterChange?.(onlyInstance, '');
    }
  }, [instances, instancesLoading, selectedInstance, defaultFiltersLoading, onFilterChange]);

  useEffect(() => {
    if (selectedInstance) {
      ensureMarketsLoaded(selectedInstance);
    } else {
      setSelectedMarket('');
    }
  }, [selectedInstance, ensureMarketsLoaded]);

  // If the selected Instance only has one Market, auto-select it - matching Suppliers/Products.
  useEffect(() => {
    if (!selectedInstance || isMarketsLoading(selectedInstance)) return;

    if (markets.length === 1) {
      const onlyMarket = markets[0].customerTypeId.toString();
      if (selectedMarket !== onlyMarket) {
        setSelectedMarket(onlyMarket);
        onFilterChange?.(selectedInstance, onlyMarket);
      }
    }
  }, [markets, selectedInstance, selectedMarket, isMarketsLoading, onFilterChange]);

  const getFilters = (): DeliveryScheduleFilterOptions => ({
    instanceId: Number(selectedInstance) || 0,
    marketId: Number(selectedMarket) || 0,
    orderTypes: selectedOrderTypes.map((x) => x.orderTypeId).join(','),
    supplierNumber: supplier,
    deliveryDateFrom,
    deliveryDateTo,
    cutOffDateFrom,
    cutOffDateTo,
    stores: selectedStores.map((x) => x.storeNumber).join(','),
    sort: sortBy,
    direction: sortDirection,
    pageNumber: currentPage,
    pageSize,
  });

  const loadFilteredData = async (options: DeliveryScheduleFilterOptions, clearSelections = true) => {
    setIsLoading(true);
    if (clearSelections) {
      setSelectedItems([]);
      setIsSelectAll(false);
    }
    try {
      const result = await client.getODS(options, selectedStore?.storeNumber ?? '');
      if (result.success && result.data) {
        setList(result.data.items ?? []);
        setTotalCount(result.data.totalCount);
      } else {
        setList([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching delivery schedules:', error);
      setList([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
      setHasSearchedOnce(true);
    }
  };

  // Options (order types/suppliers/stores) for the current Instance+Market scope.
  useEffect(() => {
    if (!selectedInstance || !selectedMarket || isMarketsLoading(selectedInstance)) {
      setOrderTypes([]);
      setSuppliers([]);
      setStores([]);
      return;
    }

    setIsOptionsLoading(true);
    Promise.all([
      client.getMarketOrderTypes({
        companyId: selectedInstance,
        customerTypeId: selectedMarket,
        pageNumber: 0,
        pageSize: 2147483647,
        sort: '',
        direction: 'asc',
      } as MarketOrderTypeFilterOptions),
      client.getMarketSuppliers({
        companyId: selectedInstance,
        customerTypeId: selectedMarket,
        pageNumber: 0,
        pageSize: 2147483647,
      }),
      client.getMarketStores({
        companyId: selectedInstance,
        customerTypeId: selectedMarket,
        searchTerm: undefined,
        pageNumber: 0,
        pageSize: 2147483647,
      } as MarketStoreFilterOptions),
    ])
      .then(([orderTypesResult, suppliersResult, storesResult]) => {
        const fetchedOrderTypes = orderTypesResult.success && orderTypesResult.data
          ? orderTypesResult.data.items.filter((x) => !isAutoShipOrder(x.orderTypeId))
          : [];
        setOrderTypes(fetchedOrderTypes);
        setSuppliers(suppliersResult.success && suppliersResult.data ? suppliersResult.data.items : []);
        setStores(storesResult.success && storesResult.data ? storesResult.data.items ?? [] : []);

        // Hydrate quick/advanced filters from the URL once, matching Products/Suppliers URL persistence.
        let hydratedOrderTypes = selectedOrderTypes;
        let hydratedSupplier = supplier;
        let hydratedDeliveryDateFrom = deliveryDateFrom;
        let hydratedDeliveryDateTo = deliveryDateTo;
        let hydratedCutOffDateFrom = cutOffDateFrom;
        let hydratedCutOffDateTo = cutOffDateTo;

        if (!hasHydratedFiltersFromUrl.current) {
          hasHydratedFiltersFromUrl.current = true;
          const initialOrderTypeIds = initialOrderTypes ? initialOrderTypes.split(',').filter(Boolean) : [];
          hydratedOrderTypes = initialOrderTypeIds.length
            ? fetchedOrderTypes.filter((x) => initialOrderTypeIds.includes(String(x.orderTypeId)))
            : [];
          hydratedSupplier = initialSupplier ?? '';
          hydratedDeliveryDateFrom = initialDeliveryDateFrom ?? '';
          hydratedDeliveryDateTo = initialDeliveryDateTo ?? '';
          hydratedCutOffDateFrom = initialCutOffDateFrom ?? '';
          hydratedCutOffDateTo = initialCutOffDateTo ?? '';

          setSelectedOrderTypes(hydratedOrderTypes);
          setSupplier(hydratedSupplier);
          setDeliveryDateFrom(hydratedDeliveryDateFrom);
          setDeliveryDateTo(hydratedDeliveryDateTo);
          setCutOffDateFrom(hydratedCutOffDateFrom);
          setCutOffDateTo(hydratedCutOffDateTo);
        }

        // Instance/Market is a scope change, not a text query - apply it immediately like Categories/Suppliers/Products do.
        setCurrentPage(0);
        loadFilteredData({
          ...getFilters(),
          orderTypes: hydratedOrderTypes.map((x) => x.orderTypeId).join(','),
          supplierNumber: hydratedSupplier,
          deliveryDateFrom: hydratedDeliveryDateFrom,
          deliveryDateTo: hydratedDeliveryDateTo,
          cutOffDateFrom: hydratedCutOffDateFrom,
          cutOffDateTo: hydratedCutOffDateTo,
          pageNumber: 0,
        });
      })
      .catch((error) => console.error('Error loading delivery schedule filter options:', error))
      .finally(() => {
        setIsOptionsLoading(false);
        setHasLoadedOptionsOnce(true);
      });
  }, [selectedInstance, selectedMarket]);

  // Full-scope stats for the headline tiles, independent of the table's own filters/paging.
  useEffect(() => {
    if (!selectedInstance || !selectedMarket) {
      setStatsList([]);
      return;
    }

    setIsStatsLoading(true);
    client.getODS({
      instanceId: Number(selectedInstance) || 0,
      marketId: Number(selectedMarket) || 0,
      orderTypes: '',
      supplierNumber: '',
      deliveryDateFrom: '',
      deliveryDateTo: '',
      cutOffDateFrom: '',
      cutOffDateTo: '',
      stores: '',
      sort: 'id',
      direction: 'asc',
      pageNumber: 0,
      pageSize: 2147483647,
    }, selectedStore?.storeNumber ?? '')
      .then((result) => {
        if (result.success && result.data) {
          setStatsList(result.data.items ?? []);
        }
      })
      .catch((error) => console.error('Error fetching delivery schedule stats:', error))
      .finally(() => setIsStatsLoading(false));
  }, [selectedInstance, selectedMarket]);

  useEffect(() => {
    if (hasSearchedOnce) {
      loadFilteredData(getFilters(), false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortBy, sortDirection]);

  useEffect(() => {
    if (!selectedOrderTypes.some((x) => isOrderTypeDSD(x.orderTypeId))) {
      setSupplier('');
    }
  }, [selectedOrderTypes]);

  const missingCutOffCount = useMemo(() => (
    statsList.filter((row) => !row.cutOffDate).length
  ), [statsList]);

  const dsdMissingSupplierCount = useMemo(() => (
    statsList.filter((row) => isOrderTypeDSD(row.orderTypeId) && !row.supplierNumber).length
  ), [statsList]);

  const upcomingDeliveriesCount = useMemo(() => {
    const now = Date.now();
    const in7Days = now + 7 * DAY_IN_MS;
    return statsList.filter((row) => {
      if (!row.deliveryDate) return false;
      const deliveryTime = new Date(row.deliveryDate).getTime();
      return !Number.isNaN(deliveryTime) && deliveryTime >= now && deliveryTime <= in7Days;
    }).length;
  }, [statsList]);

  const pastDueDeliveriesCount = useMemo(() => {
    const now = Date.now();
    return statsList.filter((row) => {
      if (!row.deliveryDate) return false;
      const deliveryTime = new Date(row.deliveryDate).getTime();
      return !Number.isNaN(deliveryTime) && deliveryTime < now;
    }).length;
  }, [statsList]);

  // Quick and Advanced filter controls both apply immediately, same as Instance/Market scope changes - no separate Search click needed.
  const applyFilters = (overrides: Partial<DeliveryScheduleFilterOptions>) => {
    if (!selectedInstance || !selectedMarket) return;
    setCurrentPage(0);
    const merged = { ...getFilters(), ...overrides, pageNumber: 0 };
    loadFilteredData(merged);
    // Persists quick/advanced filter state to the URL, matching Products/Suppliers.
    onFilterChange?.(selectedInstance, selectedMarket, {
      orderTypes: merged.orderTypes,
      supplierNumber: merged.supplierNumber,
      deliveryDateFrom: merged.deliveryDateFrom,
      deliveryDateTo: merged.deliveryDateTo,
      cutOffDateFrom: merged.cutOffDateFrom,
      cutOffDateTo: merged.cutOffDateTo,
    });
  };

  const handleSortChange = (nextSortBy: string, nextSortDirection: 'asc' | 'desc') => {
    setSortBy(nextSortBy);
    setSortDirection(nextSortDirection);
  };

  const handleSelectAllStoresChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    const newStores = checked ? stores : [];
    setSelectedStores(newStores);
    setCheckedAllStores(checked);
    applyFilters({ stores: newStores.map((x) => x.storeNumber).join(',') });
  };

  const handleSelectAllRowsChange = async (checked: boolean) => {
    setIsSelectAll(checked);
    if (!checked) {
      setSelectedItems([]);
      return;
    }

    try {
      const result = await client.getODS({ ...getFilters(), pageNumber: 0, pageSize: 2147483647 }, selectedStore?.storeNumber ?? '');
      if (result.success && result.data?.items) {
        setSelectedItems(result.data.items.filter((item) => item.id != null).map((item) => item.id!));
      } else {
        setIsSelectAll(false);
      }
    } catch (error) {
      console.error('Error selecting all delivery schedules:', error);
      setIsSelectAll(false);
    }
  };

  const handleItemSelectionChange = (itemId: number, isChecked: boolean) => {
    if (isChecked) {
      setSelectedItems((prev) => (prev.includes(itemId) ? prev : [...prev, itemId]));
    } else {
      if (isSelectAll) setIsSelectAll(false);
      setSelectedItems((prev) => prev.filter((id) => id !== itemId));
    }
  };

  const handleClearFilters = () => {
    setSelectedOrderTypes([]);
    setSupplier('');
    setSelectedStores([]);
    setCheckedAllStores(false);
    setDeliveryDateFrom('');
    setDeliveryDateTo('');
    setCutOffDateFrom('');
    setCutOffDateTo('');
    setCurrentPage(0);
    loadFilteredData({
      ...getFilters(),
      orderTypes: '',
      supplierNumber: '',
      stores: '',
      deliveryDateFrom: '',
      deliveryDateTo: '',
      cutOffDateFrom: '',
      cutOffDateTo: '',
      pageNumber: 0,
    });
    onFilterChange?.(selectedInstance, selectedMarket, {
      orderTypes: '',
      supplierNumber: '',
      deliveryDateFrom: '',
      deliveryDateTo: '',
      cutOffDateFrom: '',
      cutOffDateTo: '',
    });
  };

  const handleExportODS = async () => {
    try {
      const result = await client.exportODS(getFilters(), selectedStore?.storeNumber ?? '');
      if (result?.success) {
        const blob = base64ToBlob(result.data.fileData, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(blob);
        downloadLink.download = result.data.fileName;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    } catch (error) {
      showSnackbar(`${t('downloadFailed')} ${error}`, 'error');
    }
  };

  const handleODSUpload = async (file: File): Promise<{ success: boolean; data?: ODSImportResultModel; message?: string }> => {
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve((reader.result as string).split(',')[1]);
        reader.onerror = (error) => reject(error);
      });

      const fileRequest = { fileData: base64, fileName: file.name };
      setCurrentFileRequest(fileRequest);

      const result = await client.validateODS(fileRequest, selectedStore?.storeNumber ?? '', Number(selectedInstance) || 0, Number(selectedMarket) || 0);
      return result.success
        ? { success: true, data: result.data, message: t('validationComplete') }
        : { success: false, message: result.message || t('validationFailed') };
    } catch (error) {
      return { success: false, message: `${t('uploadFailed')}: ${error}` };
    }
  };

  const handleConfirmODSUpload = async (): Promise<{ success: boolean; message?: string }> => {
    if (!currentFileRequest) {
      return { success: false, message: t('fileRequestNotFound') };
    }

    try {
      const result = await client.importODS(currentFileRequest, selectedStore?.storeNumber ?? '', Number(selectedInstance) || 0, Number(selectedMarket) || 0, localStorage.getItem('i18nextLng') ?? 'en-GB');
      if (result.success) {
        setCurrentFileRequest(null);
        await loadFilteredData(getFilters());
        showSnackbar(t('odsChangesAppliedSuccessfully'), 'success');
        return { success: true, message: t('changesAppliedSuccessfully') };
      }
      showSnackbar(result.message || t('uploadFailed'), 'error');
      return { success: false, message: result.message || t('uploadFailed') };
    } catch (error) {
      const errorMsg = `${t('uploadFailed')}: ${error}`;
      showSnackbar(errorMsg, 'error');
      return { success: false, message: errorMsg };
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const result = await client.deleteBulkODS(selectedItems, selectedStore?.storeNumber ?? '');
      if (result.success) {
        showSnackbar(t('itemsDeletedSuccessfully'), 'success');
        await loadFilteredData(getFilters());
        setSelectedItems([]);
      } else {
        showSnackbar(result.message || t('deleteFailed'), 'error');
      }
    } catch (error) {
      showSnackbar(`${t('deleteFailed')}: ${error}`, 'error');
    } finally {
      setDeleteConfirmModalOpen(false);
    }
  };

  const handleConfirmDateSelection = async (selectedDate: string) => {
    try {
      const result = await client.updateBulkODSDate({
        ids: selectedItems,
        date: selectedDate,
        isCutOffDate: dateModalType === 'cutoff',
        companyId: Number(selectedInstance) || 0,
        languageCode: localStorage.getItem('i18nextLng') ?? 'en-GB',
      }, selectedStore?.storeNumber ?? '');

      if (result.success) {
        showSnackbar(dateModalType === 'cutoff' ? t('cutOffDateUpdatedSuccessfully') : t('deliveryDateUpdatedSuccessfully'), 'success');
        await loadFilteredData(getFilters());
        setSelectedItems([]);
      } else {
        showSnackbar(result.message || t('updateFailed'), 'error');
      }
    } catch (error) {
      showSnackbar(`${t('updateFailed')}: ${error}`, 'error');
    } finally {
      setDateModalOpen(false);
    }
  };

  const handleCreateODS = async (odsData: AddODSModel) => {
    const result = await client.addODS(odsData, selectedStore?.storeNumber ?? '');
    if (result.success) {
      showSnackbar(t('odsCreatedSuccessfully'), 'success');
      await loadFilteredData(getFilters());
    } else {
      showSnackbar(result.message || t('createODSFailed'), 'error');
      throw new Error(result.message || t('createODSFailed'));
    }
  };

  const columns: TableColumn<DeliveryScheduleModel>[] = [
    ...(hasWritePermissions ? [{
      id: 'select',
      label: (
        <Checkbox
          color="primary"
          checked={isSelectAll}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSelectAllRowsChange(e.target.checked)}
        />
      ),
      render: (row: DeliveryScheduleModel) => (
        <Checkbox
          color="primary"
          checked={selectedItems.includes(row.id)}
          onChange={(e) => handleItemSelectionChange(row.id, e.target.checked)}
        />
      ),
    }] : []),
    { id: 'id', label: t('deliveryScheduleId'), field: 'id', sortable: true },
    { id: 'storeNumber', label: t('storeNumber'), field: 'storeNumber', sortable: true },
    { id: 'storeName', label: t('storeName'), field: 'storeName' },
    { id: 'orderType', label: t('orderType'), field: 'orderType' },
    { id: 'cutOffTime', label: t('cutOffTime'), field: 'cutOffTime' },
    { id: 'cutOffDate', label: t('cutOffDate'), field: 'cutOffDate', sortable: true },
    { id: 'deliveryDate', label: t('deliveryDate'), field: 'deliveryDate', sortable: true },
    { id: 'supplierNumber', label: t('supplierNumber'), field: 'supplierNumber' },
    { id: 'supplierName', label: t('supplierName'), field: 'supplierName' },
  ];

  const bulkActions = (
    <>
      <Button
        variant="secondary"
        size="md"
        disabled={selectedItems.length === 0 || !hasWritePermissions}
        onClick={() => { setDateModalType('cutoff'); setDateModalOpen(true); }}
      >
        {t('setCutOffDate')}
      </Button>
      <Button
        variant="secondary"
        size="md"
        disabled={selectedItems.length === 0 || !hasWritePermissions}
        onClick={() => { setDateModalType('delivery'); setDateModalOpen(true); }}
      >
        {t('setDeliveryDate')}
      </Button>
      <Button
        variant="secondary"
        size="md"
        disabled={selectedItems.length === 0 || !hasDeletePermissions}
        onClick={() => setDeleteConfirmModalOpen(true)}
      >
        {t('delete')}
      </Button>
    </>
  );

  const table: TableSectionProps<DeliveryScheduleModel> = {
    title: t('deliverySchedules'),
    columns,
    rows: list,
    getRowId: (row) => row.id,
    recordCount: totalCount,
    recordCountLabel: t('deliverySchedules'),
    actions: bulkActions,
    loading: isLoading,
    sortBy,
    sortDirection,
    onSortChange: handleSortChange,
    emptyContent: (
      <EmptyTable
        bodyRowHeight={40}
        headerHeight={45}
        text={t('deliverySchedules')}
        searchTerm=""
        onClear={handleClearFilters}
        onAdd={() => setCreateODSModalOpen(true)}
        onAddIsCreating={false}
        addItemText={t('createODS')}
        showOnAdd={hasWritePermissions}
        showClear={hasSearchedOnce}
        showSecondaryText={hasSearchedOnce}
        withPaginationShown={false}
      />
    ),
    page: currentPage,
    maxPages: Math.max(1, Math.ceil(totalCount / pageSize)),
    onPageChange: setCurrentPage,
  };

  const filterBar = (
    <Grid container
      spacing={5}>
      <Grid item
        lg={6}
        sm={6}
        xs={12}>
        <FormControl fullWidth
          required>
          <InputLabel shrink>{t('filterByInstance')}</InputLabel>
          <Select
            value={selectedInstance}
            onChange={(event: SelectChangeEvent<string>) => {
              setSelectedInstance(event.target.value);
              setSelectedMarket('');
            }}
            label={t('filterByInstance')}
            displayEmpty
            notched
          >
            {instances.filter((instance) => instance.id != null).map((instance) => (
              <MenuItem key={instance.id}
                value={String(instance.id)}>
                {instance.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        lg={6}
        sm={6}
        xs={12}>
        <FormControl fullWidth
          required>
          <InputLabel shrink>{t('filterByMarket')}</InputLabel>
          <Select
            value={selectedMarket}
            onChange={(event: SelectChangeEvent<string>) => setSelectedMarket(event.target.value)}
            label={t('filterByMarket')}
            displayEmpty
            notched
            disabled={!selectedInstance}
          >
            {markets.map((market) => (
              <MenuItem key={market.customerTypeId}
                value={String(market.customerTypeId)}>
                {market.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const dateQuickFilterOptions = [
    { id: 'today', label: t('quickFilterToday', 'Today') },
    { id: 'tomorrow', label: t('quickFilterTomorrow', 'Tomorrow') },
    { id: 'thisWeek', label: t('quickFilterThisWeek', 'This Week') },
    { id: 'thisMonth', label: t('quickFilterThisMonth', 'This Month') },
  ];
  const deliveryDatePreset = findDateQuickFilterPreset(deliveryDateFrom, deliveryDateTo);
  const cutOffDatePreset = findDateQuickFilterPreset(cutOffDateFrom, cutOffDateTo);

  const quickFilterGroups: QuickFilterGroup[] = [
    {
      id: 'orderTypes',
      options: orderTypes.map((orderType) => ({ id: String(orderType.orderTypeId), label: orderType.name ?? '' })),
      value: selectedOrderTypes.map((orderType) => String(orderType.orderTypeId)),
      multiple: true,
      onChange: (value) => {
        const newOrderTypes = orderTypes.filter((orderType) => value.includes(String(orderType.orderTypeId)));
        const stillHasDsd = newOrderTypes.some((x) => isOrderTypeDSD(x.orderTypeId));
        setSelectedOrderTypes(newOrderTypes);
        if (!stillHasDsd) setSupplier('');
        applyFilters({
          orderTypes: newOrderTypes.map((x) => x.orderTypeId).join(','),
          supplierNumber: stillHasDsd ? supplier : '',
        });
      },
    },
  ];

  const supplierQuickFilterGroup: QuickFilterGroup = {
    id: 'supplier',
    options: suppliers.map((s) => ({ id: s.supplierNumber.toString(), label: s.supplierName })),
    value: supplier ? [supplier] : [],
    multiple: false,
    onChange: (value) => {
      const newSupplier = value[0] ?? '';
      setSupplier(newSupplier);
      applyFilters({ supplierNumber: newSupplier });
    },
  };

  const deliveryDateQuickFilterGroup: QuickFilterGroup = {
    id: 'deliveryDatePreset',
    options: dateQuickFilterOptions,
    value: deliveryDatePreset ? [deliveryDatePreset] : [],
    multiple: false,
    onChange: (value) => {
      const preset = value[0] as DateQuickFilterPreset | undefined;
      const [from, to] = preset ? getDateQuickFilterRange(preset) : ['', ''];
      setDeliveryDateFrom(from);
      setDeliveryDateTo(to);
      applyFilters({ deliveryDateFrom: from, deliveryDateTo: to });
    },
  };

  const cutOffDateQuickFilterGroup: QuickFilterGroup = {
    id: 'cutOffDatePreset',
    options: dateQuickFilterOptions,
    value: cutOffDatePreset ? [cutOffDatePreset] : [],
    multiple: false,
    onChange: (value) => {
      const preset = value[0] as DateQuickFilterPreset | undefined;
      const [from, to] = preset ? getDateQuickFilterRange(preset) : ['', ''];
      setCutOffDateFrom(from);
      setCutOffDateTo(to);
      applyFilters({ cutOffDateFrom: from, cutOffDateTo: to });
    },
  };

  const quickFilterSectionHeaderSx = { mb: 2, fontWeight: 600 } as const;

  const quickFilterContent = (
    <Stack spacing={4}>
      <Box>
        <Typography variant="textSM"
          sx={quickFilterSectionHeaderSx}>
          {t('orderTypes')}
        </Typography>
        <QuickFilterBar groups={quickFilterGroups} />
      </Box>
      {selectedOrderTypes.some((x) => isOrderTypeDSD(x.orderTypeId)) && (
        <Box>
          <Typography variant="textSM"
            sx={quickFilterSectionHeaderSx}>
            {t('supplier')}
          </Typography>
          <QuickFilterBar groups={[supplierQuickFilterGroup]} />
        </Box>
      )}
      <Box>
        <Typography variant="textSM"
          sx={quickFilterSectionHeaderSx}>
          {t('deliveryDate')}
        </Typography>
        <QuickFilterBar groups={[deliveryDateQuickFilterGroup]} />
      </Box>
      <Box>
        <Typography variant="textSM"
          sx={quickFilterSectionHeaderSx}>
          {t('cutOffDate')}
        </Typography>
        <QuickFilterBar groups={[cutOffDateQuickFilterGroup]} />
      </Box>
    </Stack>
  );

  const advancedFilterContent = (
    <Grid container
      spacing={5}>
      <Grid item
        lg={selectedOrderTypes.some((x) => isOrderTypeDSD(x.orderTypeId)) ? 6 : 12}
        sm={12}
        xs={12}>
        <Autocomplete
          fullWidth
          multiple
          limitTags={2}
          options={orderTypes}
          value={selectedOrderTypes}
          onChange={(_event: SyntheticEvent, value: MarketOrderType[]) => {
            const stillHasDsd = value.some((x) => isOrderTypeDSD(x.orderTypeId));
            setSelectedOrderTypes(value);
            if (!stillHasDsd) setSupplier('');
            applyFilters({
              orderTypes: value.map((x) => x.orderTypeId).join(','),
              supplierNumber: stillHasDsd ? supplier : '',
            });
          }}
          getOptionKey={(option) => option.orderTypeId}
          getOptionLabel={(option) => option.name ?? ''}
          renderInput={(params) => (
            <TextField {...params}
              label={t('selectOrderTypes')}
              placeholder={t('selectOrderTypes')} />
          )}
        />
      </Grid>
      {selectedOrderTypes.some((x) => isOrderTypeDSD(x.orderTypeId)) && (
        <Grid item
          lg={6}
          sm={12}
          xs={12}>
          <FormControl fullWidth>
            <InputLabel shrink>{t('supplier')}</InputLabel>
            <Select
              value={supplier}
              onChange={(event: SelectChangeEvent<string>) => {
                setSupplier(event.target.value);
                applyFilters({ supplierNumber: event.target.value });
              }}
              label={t('supplier')}
              displayEmpty
              notched
            >
              <MenuItem value="">{t('all')}</MenuItem>
              {suppliers.map((s) => (
                <MenuItem key={s.supplierNumber}
                  value={s.supplierNumber.toString()}>
                  {s.supplierName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
      <Grid item
        lg={9}
        sm={8}
        xs={12}>
        <Autocomplete
          fullWidth
          multiple
          limitTags={2}
          options={stores}
          value={selectedStores}
          disabled={checkedAllStores}
          onChange={(_event: SyntheticEvent, value: Store[]) => {
            setSelectedStores(value);
            applyFilters({ stores: value.map((x) => x.storeNumber).join(',') });
          }}
          getOptionKey={(option) => option.storeNumber ?? ''}
          getOptionLabel={(option) => option.storeName ?? ''}
          filterOptions={filterStoreOptions}
          renderInput={(params) => (
            <TextField {...params}
              label={t('selectStores')}
              placeholder={t('selectStores')} />
          )}
        />
      </Grid>
      <Grid item
        lg={3}
        sm={4}
        xs={12}>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox
              checked={checkedAllStores}
              onChange={handleSelectAllStoresChange} />}
            label={t('selectAll')}
          />
        </FormGroup>
      </Grid>
      <Grid item
        lg={6}
        sm={6}
        xs={12}>
        <DatePickerLocalized
          useCalendar={false}
          label={t('deliveryDateFrom')}
          format="DD/MM/YYYY"
          value={deliveryDateFrom}
          translations={{
            clearButtonLabel: t('calendar.clearButtonLabel'),
            todayButtonLabel: t('calendar.todayButtonLabel'),
            cancelButtonLabel: t('calendar.cancelButtonLabel'),
            okButtonLabel: t('calendar.okButtonLabel'),
          }}
          onChange={(newValue) => {
            const value = newValue ?? '';
            setDeliveryDateFrom(value);
            applyFilters({ deliveryDateFrom: value });
          }}
        />
      </Grid>
      <Grid item
        lg={6}
        sm={6}
        xs={12}>
        <DatePickerLocalized
          useCalendar={false}
          label={t('deliveryDateTo')}
          format="DD/MM/YYYY"
          value={deliveryDateTo}
          translations={{
            clearButtonLabel: t('calendar.clearButtonLabel'),
            todayButtonLabel: t('calendar.todayButtonLabel'),
            cancelButtonLabel: t('calendar.cancelButtonLabel'),
            okButtonLabel: t('calendar.okButtonLabel'),
          }}
          onChange={(newValue) => {
            const value = newValue ?? '';
            setDeliveryDateTo(value);
            applyFilters({ deliveryDateTo: value });
          }}
          shouldDisableDate={(date) => (deliveryDateFrom ? date < deliveryDateFrom : false)}
        />
      </Grid>
      <Grid item
        lg={6}
        sm={6}
        xs={12}>
        <DatePickerLocalized
          useCalendar={false}
          label={t('cutOffDateFrom')}
          format="DD/MM/YYYY"
          value={cutOffDateFrom}
          translations={{
            clearButtonLabel: t('calendar.clearButtonLabel'),
            todayButtonLabel: t('calendar.todayButtonLabel'),
            cancelButtonLabel: t('calendar.cancelButtonLabel'),
            okButtonLabel: t('calendar.okButtonLabel'),
          }}
          onChange={(newValue) => {
            const value = newValue ?? '';
            setCutOffDateFrom(value);
            applyFilters({ cutOffDateFrom: value });
          }}
        />
      </Grid>
      <Grid item
        lg={6}
        sm={6}
        xs={12}>
        <DatePickerLocalized
          useCalendar={false}
          label={t('cutOffDateTo')}
          format="DD/MM/YYYY"
          value={cutOffDateTo}
          translations={{
            clearButtonLabel: t('calendar.clearButtonLabel'),
            todayButtonLabel: t('calendar.todayButtonLabel'),
            cancelButtonLabel: t('calendar.cancelButtonLabel'),
            okButtonLabel: t('calendar.okButtonLabel'),
          }}
          onChange={(newValue) => {
            const value = newValue ?? '';
            setCutOffDateTo(value);
            applyFilters({ cutOffDateTo: value });
          }}
          shouldDisableDate={(date) => (cutOffDateFrom ? date < cutOffDateFrom : false)}
        />
      </Grid>
    </Grid>
  );

  const advancedFilterActions = (
    <Button
      variant="secondary"
      size="md"
      onClick={handleClearFilters}
    >
      {t('clearFilters')}
    </Button>
  );

  const actions = (
    <>
      <Button
        variant="secondary"
        size="lg"
        startIcon={<DownloadIcon />}
        disabled={!selectedInstance || !selectedMarket}
        onClick={handleExportODS}
      >
        {t('downloadODS')}
      </Button>
      <Button
        variant="secondary"
        size="lg"
        startIcon={<UploadIcon />}
        disabled={!selectedInstance || !selectedMarket || !hasWritePermissions}
        onClick={() => setUploadModalOpen(true)}
      >
        {t('uploadODS')}
      </Button>
      <Button
        variant="primary"
        size="lg"
        startIcon={<AddIcon />}
        disabled={!hasWritePermissions}
        onClick={() => setCreateODSModalOpen(true)}
      >
        {t('createODS')}
      </Button>
    </>
  );

  const headlineContent: ListPageHeadline = {
    insights: [
      <InsightTile
        key="dsdMissingSupplier"
        label={t('procurementTileDsdMissingSupplier', 'DSD Missing Supplier')}
        value={dsdMissingSupplierCount}
        description={t('procurementTileDsdMissingSupplierDescription', 'DSD schedules with no supplier assigned')}
        icon={<PersonOff />}
        variant="error"
        loading={isStatsLoading}
      />,
      <InsightTile
        key="missingCutOff"
        label={t('procurementTileMissingCutOffDate', 'Missing Cutoff Date')}
        value={missingCutOffCount}
        description={t('procurementTileMissingCutOffDateDescription', 'Schedules with no cutoff date set')}
        icon={<EventBusy />}
        variant="warning"
        loading={isStatsLoading}
      />,
      <InsightTile
        key="upcomingDeliveries"
        label={t('procurementTileUpcomingDeliveries', 'Upcoming Deliveries (7 Days)')}
        value={upcomingDeliveriesCount}
        description={t('procurementTileUpcomingDeliveriesDescription', 'Deliveries scheduled in the next 7 days')}
        icon={<LocalShipping />}
        variant="secondary"
        loading={isStatsLoading}
      />,
      <InsightTile
        key="pastDueDeliveries"
        label={t('procurementTilePastDueDeliveries', 'Past Due Deliveries')}
        value={pastDueDeliveriesCount}
        description={t('procurementTilePastDueDeliveriesDescription', 'Delivery date has passed for these schedules')}
        icon={<ReportProblem />}
        variant="error"
        loading={isStatsLoading}
      />,
    ],
  };

  return (
    <>
      {children({
        filterBar,
        quickFilterContent,
        advancedFilterContent,
        advancedFilterActions,
        actions,
        headlineContent,
        table,
        // Still resolving the default instance/market or the option lists before any data is meaningful - keep showing the skeleton.
        isInitialLoading: (defaultFiltersLoading || isOptionsLoading) && !hasLoadedOptionsOnce,
      })}

      <MessagesSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
        duration={snackbarSeverity === 'error' ? 15000 : 5000}
      />

      <ODSUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onFileSelected={handleODSUpload}
        onConfirmUpload={handleConfirmODSUpload}
        title={t('uploadODSFile')}
        uploadLabel={t('dragDropODSFileHere')}
        uploadSublabel={t('acceptsExcelFiles')}
      />

      <DateSelectionModal
        open={dateModalOpen}
        onClose={() => setDateModalOpen(false)}
        onConfirm={handleConfirmDateSelection}
        selectedItemsCount={selectedItems.length}
        dateType={dateModalType}
      />

      <ConfirmationDialog
        open={deleteConfirmModalOpen}
        message={t('deleteModalMessage')}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirmModalOpen(false)}
        icon={<Trash01 />}
        title={t('deleteConfirmText')}
        variant="bad"
        confirmButtonText={t('confirm')}
        cancelButtonText={t('cancel')}
      />

      <AddODSModal
        open={createODSModalOpen}
        onClose={() => setCreateODSModalOpen(false)}
        onConfirm={handleCreateODS}
        title={t('createNewODS')}
        instances={instances}
      />
    </>
  );
};

export default DeliverySchedulesListPageContent;
